/**
 * GWP (Gift With Purchase) Cart Banner - v4
 * 
 * Custom element handling free-gift logic in the cart.
 * 
 * v4 changes:
 * - Celebration shimmer fires once per session (not on every re-render)
 * - Reliable variant selection after add/remove cycles
 * - Selected variant name updates inline
 * - Variant image swaps on swatch click
 */

(function() {
  'use strict';
  
  if (customElements.get('gwp-cart-banner')) return;
  
  const SESSION_KEY = 'gwp_celebrated_v1';
  
  class GwpCartBanner extends HTMLElement {
    constructor() {
      super();
      this.handleClaim = this.handleClaim.bind(this);
      this.handleVariantSelect = this.handleVariantSelect.bind(this);
      this.handleVariantKeydown = this.handleVariantKeydown.bind(this);
      this.handleCartUpdate = this.handleCartUpdate.bind(this);
      this.selectedVariantId = null;
      this.variantImageMap = null;
      this.cartListenerAttached = false;
    }
    
    connectedCallback() {
      this.thresholdCents = parseInt(this.dataset.threshold, 10);
      this.productHandle = this.dataset.productHandle;
      this.productId = this.dataset.productId;
      this.inventoryCap = parseInt(this.dataset.inventoryCap, 10);
      this.thresholdMet = this.dataset.thresholdMet === 'true';
      this.giftInCart = this.dataset.giftInCart === 'true';
      this.giftLineKey = this.dataset.giftLineKey || '';
      
      this.selectedVariantId = null;
      this.variantImageMap = null;
      
      this.parseVariantImageMap();
      this.setupVariantSelector();
      this.setupClaimButton();
      this.setupCartListeners();
      this.checkBelowThreshold();
      this.maybePlayCelebration();
    }
    
    disconnectedCallback() {
      if (this.cartListenerAttached) {
        document.documentElement.removeEventListener('cart:updated', this.handleCartUpdate);
        this.cartListenerAttached = false;
      }
    }
    
    /**
     * Play the celebration shimmer once per session when threshold is first hit.
     */
    maybePlayCelebration() {
      if (!this.thresholdMet) return;
      
      let hasCelebrated = false;
      try {
        hasCelebrated = sessionStorage.getItem(SESSION_KEY) === 'true';
      } catch (err) {
        // sessionStorage may be blocked in some browsers (e.g. Safari private mode)
        hasCelebrated = false;
      }
      
      if (!hasCelebrated) {
        this.classList.add('gwp-banner--celebrate');
        try {
          sessionStorage.setItem(SESSION_KEY, 'true');
        } catch (err) {
          // Ignore - just won't persist across renders if storage is blocked
        }
        
        // Remove the class after animation completes so it doesn't keep playing
        // if the element gets re-rendered with the class still applied
        setTimeout(function() {
          this.classList.remove('gwp-banner--celebrate');
        }.bind(this), 1700);
      }
    }
    
    parseVariantImageMap() {
      const dataScript = this.querySelector('[data-gwp-variant-images]');
      if (!dataScript) return;
      
      try {
        const text = dataScript.textContent.trim();
        if (text) {
          this.variantImageMap = JSON.parse(text);
        }
      } catch (err) {
        console.error('GWP: Failed to parse variant image map', err);
        this.variantImageMap = null;
      }
    }
    
    setupVariantSelector() {
      const variantContainer = this.querySelector('[data-gwp-variants]');
      if (!variantContainer) {
        const claimBtn = this.querySelector('[data-gwp-claim]');
        if (claimBtn) {
          this.selectedVariantId = claimBtn.dataset.defaultVariantId;
        }
        return;
      }
      
      const swatches = variantContainer.querySelectorAll('.gwp-banner__swatch');
      
      const firstSelected = variantContainer.querySelector('[aria-checked="true"]');
      if (firstSelected) {
        this.selectedVariantId = firstSelected.dataset.variantId;
      } else if (swatches.length > 0) {
        this.selectedVariantId = swatches[0].dataset.variantId;
        swatches[0].setAttribute('aria-checked', 'true');
        swatches[0].classList.add('gwp-banner__swatch--selected');
      }
      
      swatches.forEach(function(swatch) {
        swatch.addEventListener('click', this.handleVariantSelect);
        swatch.addEventListener('keydown', this.handleVariantKeydown);
      }.bind(this));
    }
    
    handleVariantKeydown(e) {
      const swatch = e.currentTarget;
      
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        swatch.click();
        return;
      }
      
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        const next = swatch.nextElementSibling;
        if (next && next.classList.contains('gwp-banner__swatch')) {
          next.focus();
          next.click();
        }
      }
      
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = swatch.previousElementSibling;
        if (prev && prev.classList.contains('gwp-banner__swatch')) {
          prev.focus();
          prev.click();
        }
      }
    }
    
    handleVariantSelect(e) {
      const button = e.currentTarget;
      const allSwatches = this.querySelectorAll('.gwp-banner__swatch');
      
      allSwatches.forEach(function(opt) {
        opt.setAttribute('aria-checked', 'false');
        opt.classList.remove('gwp-banner__swatch--selected');
      });
      
      button.setAttribute('aria-checked', 'true');
      button.classList.add('gwp-banner__swatch--selected');
      this.selectedVariantId = button.dataset.variantId;
      
      // Update the inline selected name display
      const nameDisplay = this.querySelector('[data-gwp-selected-name]');
      if (nameDisplay && button.dataset.variantTitle) {
        nameDisplay.textContent = '— ' + button.dataset.variantTitle;
      }
      
      this.updateProductImage(this.selectedVariantId);
    }
    
    updateProductImage(variantId) {
      if (!this.variantImageMap) return;
      
      const imageContainer = this.querySelector('[data-gwp-product-image]');
      const imageEl = this.querySelector('[data-gwp-image-target]');
      if (!imageEl || !imageContainer) return;
      
      const variantData = this.variantImageMap[variantId];
      if (!variantData || !variantData.image) return;
      
      imageContainer.classList.add('gwp-banner__image--swapping');
      
      const newImage = new Image();
      newImage.onload = function() {
        imageEl.src = variantData.image;
        if (variantData.image_2x) {
          imageEl.srcset = variantData.image + ' 1x, ' + variantData.image_2x + ' 2x';
        }
        if (variantData.title) {
          imageEl.alt = variantData.title;
        }
        requestAnimationFrame(function() {
          imageContainer.classList.remove('gwp-banner__image--swapping');
        });
      };
      newImage.onerror = function() {
        imageEl.src = variantData.image;
        imageContainer.classList.remove('gwp-banner__image--swapping');
      };
      newImage.src = variantData.image;
    }
    
    setupClaimButton() {
      const claimBtn = this.querySelector('[data-gwp-claim]');
      if (!claimBtn) return;
      
      claimBtn.addEventListener('click', this.handleClaim);
    }
    
    async handleClaim(e) {
      const claimBtn = e.currentTarget;
      
      if (!this.selectedVariantId) {
        this.selectedVariantId = claimBtn.dataset.defaultVariantId;
      }
      
      if (!this.selectedVariantId) {
        console.error('GWP: No variant selected and no default available');
        return;
      }
      
      claimBtn.setAttribute('aria-busy', 'true');
      claimBtn.disabled = true;
      
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: parseInt(this.selectedVariantId, 10),
            quantity: 1,
            properties: {
              '_gwp_gift': 'true',
              '_gwp_threshold': (this.thresholdCents / 100).toString()
            },
            sections: ['mini-cart']
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(function() { return {}; });
          throw new Error(errorData.description || 'Failed to add gift to cart');
        }
        
        const cartResponse = await response.json();
        
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'gwp_claimed', {
            event_label: 'Mothers Day GWP',
            variant_id: this.selectedVariantId,
            threshold_dollars: this.thresholdCents / 100
          });
        }
        
        const cartFetch = await fetch('/cart.js');
        const cartContent = await cartFetch.json();
        
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cartContent }
        }));
        
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart: cartResponse, openMiniCart: false }
        }));
        
      } catch (error) {
        console.error('GWP claim error:', error);
        alert('There was a problem adding your free gift. Please try again or contact support.');
        claimBtn.removeAttribute('aria-busy');
        claimBtn.disabled = false;
      }
    }
    
    setupCartListeners() {
      if (this.cartListenerAttached) return;
      document.documentElement.addEventListener('cart:updated', this.handleCartUpdate);
      this.cartListenerAttached = true;
    }
    
    /**
     * Compute eligible subtotal (excluding gift line + excluded-tagged items).
     * Mirrors the Liquid logic in gwp-cart-banner.liquid.
     */
    computeEligibleSubtotal(cart) {
      const excludedRaw = (this.dataset.excludedTags || '').trim();
      const excludedTags = excludedRaw
        ? excludedRaw.split(',').map(function(t) { return t.trim().toLowerCase(); }).filter(Boolean)
        : [];
      
      let eligibleSubtotal = 0;
      cart.items.forEach(function(item) {
        const isGift = item.properties && item.properties._gwp_gift === 'true';
        if (isGift) return;
        
        const itemTags = (item.product_tags || []).map(function(t) {
          return String(t).toLowerCase();
        });
        const isExcluded = excludedTags.some(function(tag) {
          return itemTags.indexOf(tag) !== -1;
        });
        if (isExcluded) return;
        
        eligibleSubtotal += item.final_line_price;
      });
      return eligibleSubtotal;
    }
    
    /**
     * Format cents as a money string matching Liquid's `money_without_trailing_zeros`.
     * Falls back to a simple US-style "$N" / "$N.NN" formatter.
     */
    formatMoneyNoTrailingZeros(cents) {
      const amount = cents / 100;
      const isWhole = cents % 100 === 0;
      const fmt = isWhole ? amount.toFixed(0) : amount.toFixed(2);
      const withCommas = fmt.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return '$' + withCommas;
    }
    
    /**
     * Update the visible progress UI based on a fresh cart payload.
     * Only handles within-mode updates (progress text + bar). Mode switches
     * (progress -> success or success -> progress) require the next page load
     * to re-render the markup.
     */
    updateProgressDisplay(cart) {
      const eligibleSubtotal = this.computeEligibleSubtotal(cart);
      const progressEl = this.querySelector('[data-gwp-state="progress"]');
      
      // Only update DOM if we're currently in progress mode
      if (!progressEl) return;
      
      const remainingCents = Math.max(0, this.thresholdCents - eligibleSubtotal);
      const percent = Math.min(100, (eligibleSubtotal * 100) / this.thresholdCents);
      
      const textEl = progressEl.querySelector('.gwp-banner__progress-text');
      const fillEl = progressEl.querySelector('.gwp-banner__progress-fill');
      const barEl = progressEl.querySelector('.gwp-banner__progress-bar');
      
      if (textEl) {
        const template = this.dataset.progressTemplate || '[amount] away from a free [product]';
        const productTitle = this.dataset.productTitle || '';
        const remaining = this.formatMoneyNoTrailingZeros(remainingCents);
        textEl.textContent = template
          .replace('[amount]', remaining)
          .replace('[product]', productTitle);
      }
      
      if (fillEl) {
        fillEl.style.width = percent + '%';
      }
      
      if (barEl) {
        barEl.setAttribute('aria-valuenow', String(Math.round(percent)));
      }
    }
    
    async handleCartUpdate(e) {
      const cart = e.detail && e.detail.cart;
      if (!cart) return;
      
      // Always refresh the visible progress state so banners stay in sync
      // when items are added/removed elsewhere on the page (e.g. cart drawer
      // affecting a PDP-rendered banner).
      this.updateProgressDisplay(cart);
      
      const giftItem = cart.items.find(function(item) {
        return item.properties && item.properties._gwp_gift === 'true';
      });
      
      if (!giftItem) {
        // Gift no longer in cart - clear celebration flag so it can fire again
        // if customer hits threshold again later in same session
        try {
          sessionStorage.removeItem(SESSION_KEY);
        } catch (err) {
          // Ignore storage errors
        }
        return;
      }
      
      const eligibleSubtotal = this.computeEligibleSubtotal(cart);
      
      if (eligibleSubtotal < this.thresholdCents) {
        await this.removeGift(giftItem.key);
      }
    }
    
    checkBelowThreshold() {
      if (this.giftInCart && !this.thresholdMet) {
        fetch('/cart.js')
          .then(function(r) { return r.json(); })
          .then(function(cart) {
            const giftItem = cart.items.find(function(item) {
              return item.properties && item.properties._gwp_gift === 'true';
            });
            
            if (giftItem) {
              let eligibleSubtotal = 0;
              cart.items.forEach(function(item) {
                const isGift = item.properties && item.properties._gwp_gift === 'true';
                if (!isGift) {
                  eligibleSubtotal += item.final_line_price;
                }
              });
              
              if (eligibleSubtotal < this.thresholdCents) {
                this.removeGift(giftItem.key);
              }
            }
          }.bind(this))
          .catch(function(err) {
            console.error('GWP reconciliation error:', err);
          });
      }
    }
    
    async removeGift(lineKey) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: lineKey,
            quantity: 0,
            sections: ['mini-cart']
          })
        });
        
        if (!response.ok) throw new Error('Failed to remove gift');
        
        const cartResponse = await response.json();
        
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cartResponse }
        }));
        
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart: cartResponse, openMiniCart: false }
        }));
        
      } catch (error) {
        console.error('GWP auto-remove error:', error);
      }
    }
  }
  
  customElements.define('gwp-cart-banner', GwpCartBanner);
})();